import { api } from "#/lib/api-client";
import type {
  DeptFeedback, DeptFeedbackCreateRequest,
  GRN, GRNCreateRequest,
  LPO, LPOCreateRequest, LpoDraftResponse,
  NCR, NCRCreateRequest,
  PageResponse,
  QuotationEvaluation, QuotationEvaluationCreateRequest,
  Requisition, RequisitionApprovalStage, RequisitionCreateRequest,
  RFQ, RFQCreateRequest,
  RiskRegisterCreateRequest, RiskRegisterEntry,
  Supplier, SupplierCreateRequest, SupplierOption,
  SupplierDelisting, SupplierDelistingCreateRequest,
  SupplierEvaluation, SupplierEvaluationCreateRequest,
  SupplierPrequal, SupplierPrequalCreateRequest,
  TenderRegisterCreateRequest, TenderRegisterEntry,
} from "./types";

const B = "/api/procurement";

function p(params: Record<string, unknown>) {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== "") out[k] = String(v);
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

// ─── PROC-FRM-01 Requisitions ─────────────────────────────────────────────

export const requisitionApi = {
  list(params: { status?: string; departmentId?: number; page?: number; size?: number } = {}) {
    return api.get(`${B}/requisitions`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<Requisition>(r.data));
  },
  get(id: number) { return api.get<Requisition>(`${B}/requisitions/${id}`).then(r => r.data); },
  create(payload: RequisitionCreateRequest) { return api.post<Requisition>(`${B}/requisitions`, payload).then(r => r.data); },
  // NOTE: there is no PUT /{id} on this backend — do not add an `update()` here. Any field
  // corrections (e.g. budgetLine at the FINANCE stage) go through `approve()` below instead.
  submit(id: number) { return api.post<Requisition>(`${B}/requisitions/${id}/submit`).then(r => r.data); },
  /**
   * `budgetLine` is only meaningful (and only accepted server-side) when `stage === "FINANCE"` —
   * it updates the budget line and advances the status in one atomic call, gated purely by
   * PROCUREMENT_APPROVE (no separate PROCUREMENT_CREATE / update endpoint needed).
   */
  approve(id: number, stage: RequisitionApprovalStage, comments?: string, budgetLine?: string) {
    return api.post<Requisition>(`${B}/requisitions/${id}/approve`, { comments }, { params: { stage, budgetLine } }).then(r => r.data);
  },
  reject(id: number, reason: string) { return api.post<Requisition>(`${B}/requisitions/${id}/reject`, { reason }).then(r => r.data); },
  /**
   * Confirmed 2026-07-10 — distinct from reject(): reject() enforces the caller is the *current*
   * approver at whatever workflow step the PR is on; cancel() has no such check — anyone holding
   * PROCUREMENT_APPROVE can cancel at any point except MD_APPROVED/CANCELLED. Takes `reason` as
   * a query param, not a JSON body (unlike reject).
   */
  cancel(id: number, reason?: string) {
    return api.post<Requisition>(`${B}/requisitions/${id}/cancel`, null, { params: p({ reason }) }).then(r => r.data);
  },
};

// ─── PROC-FRM-02 RFQs ────────────────────────────────────────────────────

export const rfqApi = {
  list(params: { status?: string; page?: number; size?: number } = {}) {
    return api.get(`${B}/rfqs`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<RFQ>(r.data));
  },
  get(id: number) { return api.get<RFQ>(`${B}/rfqs/${id}`).then(r => r.data); },
  /** RFQs sent but not yet evaluated — the exact pool for the Evaluation form's "Linked RFQ" dropdown. */
  pendingEvaluation(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/rfqs/pending-evaluation`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 50 }) }).then(r => normalizePage<RFQ>(r.data));
  },
  create(payload: RFQCreateRequest) { return api.post<RFQ>(`${B}/rfqs`, payload).then(r => r.data); },
  send(id: number) { return api.post<RFQ>(`${B}/rfqs/${id}/send`).then(r => r.data); },
  close(id: number) { return api.post<RFQ>(`${B}/rfqs/${id}/close`).then(r => r.data); },
};

// ─── PROC-FRM-03 Evaluations ──────────────────────────────────────────────

export const evaluationApi = {
  list(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/evaluations`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<QuotationEvaluation>(r.data));
  },
  get(id: number) { return api.get<QuotationEvaluation>(`${B}/evaluations/${id}`).then(r => r.data); },
  create(payload: QuotationEvaluationCreateRequest) { return api.post<QuotationEvaluation>(`${B}/evaluations`, payload).then(r => r.data); },
  approve(id: number) { return api.post<QuotationEvaluation>(`${B}/evaluations/${id}/approve`).then(r => r.data); },
  reject(id: number) { return api.post<QuotationEvaluation>(`${B}/evaluations/${id}/reject`).then(r => r.data); },
  /** Pre-filled LPO starting point from an APPROVED evaluation (400 otherwise). */
  lpoDraft(evaluationId: number) { return api.get<LpoDraftResponse>(`${B}/evaluations/${evaluationId}/lpo-draft`).then(r => r.data); },
};

// ─── PROC-FRM-04 LPOs ────────────────────────────────────────────────────

export const lpoApi = {
  list(params: { status?: string; page?: number; size?: number } = {}) {
    return api.get(`${B}/lpos`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<LPO>(r.data));
  },
  get(id: number) { return api.get<LPO>(`${B}/lpos/${id}`).then(r => r.data); },
  create(payload: LPOCreateRequest) { return api.post<LPO>(`${B}/lpos`, payload).then(r => r.data); },
  authorize(id: number) { return api.post<LPO>(`${B}/lpos/${id}/authorize`).then(r => r.data); },
  send(id: number) { return api.post<LPO>(`${B}/lpos/${id}/send`).then(r => r.data); },
  /** V69 — Export LPO register as Excel */
  export(params: { status?: string } = {}): Promise<Blob> {
    return api.get<Blob>(`${B}/lpos/export`, { responseType: "blob", params: p(params) }).then(r => r.data as Blob);
  },
};

// ─── PROC-FRM-05 GRNs ────────────────────────────────────────────────────

export const grnApi = {
  list(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/grns`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<GRN>(r.data));
  },
  get(id: number) { return api.get<GRN>(`${B}/grns/${id}`).then(r => r.data); },
  create(payload: GRNCreateRequest) { return api.post<GRN>(`${B}/grns`, payload).then(r => r.data); },
  accept(id: number) { return api.post<GRN>(`${B}/grns/${id}/accept`).then(r => r.data); },
  reject(id: number) { return api.post<GRN>(`${B}/grns/${id}/reject`).then(r => r.data); },
  /** V69 — Export GRN register as Excel */
  export(): Promise<Blob> {
    return api.get<Blob>(`${B}/grns/export`, { responseType: "blob" }).then(r => r.data as Blob);
  },
};

// ─── PROC-FRM-06 NCRs ────────────────────────────────────────────────────

export const ncrApi = {
  list(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/ncrs`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<NCR>(r.data));
  },
  get(id: number) { return api.get<NCR>(`${B}/ncrs/${id}`).then(r => r.data); },
  create(payload: NCRCreateRequest) { return api.post<NCR>(`${B}/ncrs`, payload).then(r => r.data); },
  resolve(id: number, correctiveAction?: string) { return api.post<NCR>(`${B}/ncrs/${id}/resolve`, { correctiveAction }).then(r => r.data); },
  close(id: number, remarks?: string) { return api.post<NCR>(`${B}/ncrs/${id}/close`, { remarks }).then(r => r.data); },
};

// ─── PROC-FRM-07 Supplier Prequalification ───────────────────────────────

export const prequalApi = {
  list(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/supplier-prequal`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<SupplierPrequal>(r.data));
  },
  get(id: number) { return api.get<SupplierPrequal>(`${B}/supplier-prequal/${id}`).then(r => r.data); },
  create(payload: SupplierPrequalCreateRequest) { return api.post<SupplierPrequal>(`${B}/supplier-prequal`, payload).then(r => r.data); },
};

// ─── PROC-FRM-08 Supplier Evaluation ─────────────────────────────────────

export const supplierEvalApi = {
  list(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/supplier-eval`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<SupplierEvaluation>(r.data));
  },
  get(id: number) { return api.get<SupplierEvaluation>(`${B}/supplier-eval/${id}`).then(r => r.data); },
  create(payload: SupplierEvaluationCreateRequest) { return api.post<SupplierEvaluation>(`${B}/supplier-eval`, payload).then(r => r.data); },
};

// ─── PROC-FRM-10 Suppliers ────────────────────────────────────────────────

export const supplierApi = {
  list(params: { status?: string; page?: number; size?: number } = {}) {
    return api.get(`${B}/suppliers`, { params: p({ ...params, page: params.page ?? 0, size: params.size ?? 10 }) }).then(r => normalizePage<Supplier>(r.data));
  },
  get(id: number) { return api.get<Supplier>(`${B}/suppliers/${id}`).then(r => r.data); },
  create(payload: SupplierCreateRequest) { return api.post<Supplier>(`${B}/suppliers`, payload).then(r => r.data); },
  update(id: number, payload: Partial<SupplierCreateRequest>) { return api.put<Supplier>(`${B}/suppliers/${id}`, payload).then(r => r.data); },
  activate(id: number) { return api.post<Supplier>(`${B}/suppliers/${id}/activate`).then(r => r.data); },
  suspend(id: number) { return api.post<Supplier>(`${B}/suppliers/${id}/suspend`).then(r => r.data); },
  /**
   * All active suppliers as a lightweight list for dropdowns — id/name/email only (confirmed
   * 2026-07-09, see SupplierOption in types.ts). The fallback below only kicks in if this real
   * endpoint is ever unreachable again; when it does, it maps the full Supplier list down to
   * the same lightweight shape rather than leaking code/address/etc. to callers that shouldn't
   * rely on them being present.
   */
  options() {
    return api.get<SupplierOption[]>(`${B}/suppliers/options`)
      .catch(() => api.get<PageResponse<Supplier>>(`${B}/suppliers`, { params: { size: 200, status: "ACTIVE" } })
        .then(r => ({ data: r.data.content.map((s): SupplierOption => ({ id: s.id, name: s.name, email: s.email ?? null })) })))
      .then(r => r.data);
  },
};

// ─── Supplier Contacts & Compliance Documents (HR module path) ────────────

const HR_SUPPLIER_B = "/api/hr/procurement/suppliers";

export type SupplierContact = {
  id: number;
  fullName: string;              // API field: fullName
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  primary?: boolean;             // API field: primary
  receiveRfq?: boolean;
  receivePo?: boolean;
  receiveAlerts?: boolean;
};

export type SupplierContactRequest = Omit<SupplierContact, "id">;

export type SupplierDocument = {
  id: number;
  documentType: "TAX_COMPLIANCE" | "VAT_CERT" | "CR12" | "PREQUALIFICATION" | "OTHER" | string;
  documentNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  issuingAuthority?: string | null;
  filePath?: string | null;
  notes?: string | null;
  fileName?: string;
  uploadedAt?: string;
};

export const supplierContactApi = {
  list(supplierId: number): Promise<SupplierContact[]> {
    return api.get<SupplierContact[]>(`${HR_SUPPLIER_B}/${supplierId}/contacts`).then(r => r.data);
  },
  create(supplierId: number, payload: SupplierContactRequest): Promise<SupplierContact> {
    return api.post<SupplierContact>(`${HR_SUPPLIER_B}/${supplierId}/contacts`, payload).then(r => r.data);
  },
  update(supplierId: number, contactId: number, payload: SupplierContactRequest): Promise<SupplierContact> {
    return api.put<SupplierContact>(`${HR_SUPPLIER_B}/${supplierId}/contacts/${contactId}`, payload).then(r => r.data);
  },
  delete(supplierId: number, contactId: number): Promise<void> {
    return api.delete(`${HR_SUPPLIER_B}/${supplierId}/contacts/${contactId}`).then(() => undefined);
  },
};

export const supplierDocApi = {
  list(supplierId: number): Promise<SupplierDocument[]> {
    return api.get<SupplierDocument[]>(`${HR_SUPPLIER_B}/${supplierId}/documents`).then(r => r.data);
  },
  upload(supplierId: number, file: File, documentType: string, expiryDate?: string): Promise<SupplierDocument> {
    const form = new FormData();
    form.append("file", file);
    form.append("documentType", documentType);
    if (expiryDate) form.append("expiryDate", expiryDate);
    return api.post<SupplierDocument>(`${HR_SUPPLIER_B}/${supplierId}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  update(supplierId: number, docId: number, payload: { documentType?: string; expiryDate?: string | null }): Promise<SupplierDocument> {
    return api.put<SupplierDocument>(`${HR_SUPPLIER_B}/${supplierId}/documents/${docId}`, payload).then(r => r.data);
  },
};

// ─── PDF Downloads — exact paths from API contract ─────────────────────────
// Individual-record PDFs (require an ID)
// Register PDFs (no ID — full register download)

export const procPdfApi = {
  requisition: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/requisitions/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  rfq: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/rfqs/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  evaluation: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/evaluations/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  lpo: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/lpos/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  grn: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/grns/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  ncr: (id: number): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/ncrs/${id}/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  // Register-level PDFs (no {id})
  riskRegister: (): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/risk-register/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  feedback: (): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/feedback/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  delisting: (): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/delisting/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  tenderOpening: (): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/tender-opening/pdf`, { responseType: "blob" }).then(r => r.data as Blob),
  /** New 2026-07-08 — one workbook covering every procurement submodule (Summary + one sheet each). */
  masterReport: (): Promise<Blob> =>
    api.get<Blob>(`/api/procurement/reports/master/export`, { responseType: "blob" }).then(r => r.data as Blob),
};

// ─── Admin forms ──────────────────────────────────────────────────────────

export const adminProcApi = {
  // PROC-FRM-09 Department User Feedback — /api/procurement/feedback
  listFeedback(p2: { page?: number; size?: number } = {}) { return api.get(`${B}/feedback`, { params: p({ ...p2, page: p2.page ?? 0, size: p2.size ?? 10 }) }).then(r => normalizePage<DeptFeedback>(r.data)); },
  createFeedback(payload: DeptFeedbackCreateRequest) { return api.post<DeptFeedback>(`${B}/feedback`, payload).then(r => r.data); },
  getFeedback(id: number) { return api.get<DeptFeedback>(`${B}/feedback/${id}`).then(r => r.data); },

  // PROC-FRM-10 Tender Opening Register — /api/procurement/tender-opening
  listTenders(p2: { page?: number; size?: number } = {}) { return api.get(`${B}/tender-opening`, { params: p({ ...p2, page: p2.page ?? 0, size: p2.size ?? 10 }) }).then(r => normalizePage<TenderRegisterEntry>(r.data)); },
  createTender(payload: TenderRegisterCreateRequest) { return api.post<TenderRegisterEntry>(`${B}/tender-opening`, payload).then(r => r.data); },

  // PROC-FRM-11 Risk Register — /api/procurement/risk-register
  listRisks(p2: { page?: number; size?: number } = {}) { return api.get(`${B}/risk-register`, { params: p({ ...p2, page: p2.page ?? 0, size: p2.size ?? 10 }) }).then(r => normalizePage<RiskRegisterEntry>(r.data)); },
  createRisk(payload: RiskRegisterCreateRequest) { return api.post<RiskRegisterEntry>(`${B}/risk-register`, payload).then(r => r.data); },

  // PROC-FRM-12 Supplier Delisting — /api/procurement/delisting
  listDelistings(p2: { page?: number; size?: number } = {}) { return api.get(`${B}/delisting`, { params: p({ ...p2, page: p2.page ?? 0, size: p2.size ?? 10 }) }).then(r => normalizePage<SupplierDelisting>(r.data)); },
  getDelisting(id: number) { return api.get<SupplierDelisting>(`${B}/delisting/${id}`).then(r => r.data); },
  createDelisting(payload: SupplierDelistingCreateRequest) { return api.post<SupplierDelisting>(`${B}/delisting`, payload).then(r => r.data); },
  approveDelisting(id: number) { return api.post<SupplierDelisting>(`${B}/delisting/${id}/approve`).then(r => r.data); },
  rejectDelisting(id: number) { return api.post<SupplierDelisting>(`${B}/delisting/${id}/reject`).then(r => r.data); },
};
